import { useEffect, useState, useRef } from "react";
import { useNavigate, Link } from "react-router-dom";
import { ArrowLeft, Loader2, Lock } from "lucide-react";
import { toast } from "sonner";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { RadioGroup, RadioGroupItem } from "../components/ui/radio-group";
import { Button } from "../components/ui/button";
import { useCart } from "../context/CartContext";
import { money } from "../lib/menu";
import { api, formatApiError } from "../lib/api";

export default function Checkout() {
  const { items, totalCents, clear } = useCart();
  const navigate = useNavigate();
  const [name, setName] = useState("");
  const [phone, setPhone] = useState("");
  const [email, setEmail] = useState("");
  const [pickupType, setPickupType] = useState("asap");
  const [pickupTime, setPickupTime] = useState("");
  const [card, setCard] = useState(null);
  const [sdkReady, setSdkReady] = useState(false);
  const [paying, setPaying] = useState(false);
  const [configured, setConfigured] = useState(true);
  const cardRef = useRef(null);

  useEffect(() => {
    if (items.length === 0) navigate("/order");
  }, [items.length, navigate]);

  useEffect(() => {
    let cancelled = false;
    let cardInstance = null;

    const loadSdk = (src) =>
      new Promise((resolve, reject) => {
        if (window.Square) return resolve();
        const existing = document.querySelector('script[src*="squarecdn.com"]');
        if (existing) {
          existing.addEventListener("load", () => resolve());
          existing.addEventListener("error", reject);
          return;
        }
        const script = document.createElement("script");
        script.src = src;
        script.async = true;
        script.onload = () => resolve();
        script.onerror = reject;
        document.body.appendChild(script);
      });

    (async () => {
      try {
        const { data: cfg } = await api.get("/config");
        if (!cfg.squareConfigured) { setConfigured(false); return; }
        const src = cfg.squareEnvironment === "production"
          ? "https://web.squarecdn.com/v1/square.js"
          : "https://sandbox.web.squarecdn.com/v1/square.js";
        await loadSdk(src);
        if (cancelled) return;

        const payments = window.Square.payments(cfg.squareApplicationId, cfg.squareLocationId);
        const container = document.getElementById("card-container");
        if (container) container.innerHTML = ""; // clear any stale iframe
        cardInstance = await payments.card();
        if (cancelled) { try { await cardInstance.destroy(); } catch (_) { /* noop */ } return; }
        await cardInstance.attach("#card-container");
        cardRef.current = cardInstance;
        setCard(cardInstance);
        setSdkReady(true);
      } catch (e) {
        if (!cancelled) toast.error("Could not load payment form.");
      }
    })();

    return () => {
      cancelled = true;
      const inst = cardInstance || cardRef.current;
      if (inst) { try { inst.destroy(); } catch (_) { /* noop */ } }
      cardRef.current = null;
      const container = document.getElementById("card-container");
      if (container) container.innerHTML = "";
    };
  }, []);

  const handlePay = async () => {
    if (!name.trim() || !phone.trim()) return toast.error("Please enter your name and phone.");
    if (!email.trim() || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim())) return toast.error("Please enter a valid email for your receipt.");
    if (pickupType === "scheduled" && !pickupTime) return toast.error("Please choose a pickup time.");
    if (!card) return toast.error("Payment form is still loading.");
    setPaying(true);
    try {
      const { data: order } = await api.post("/orders", {
        customer_name: name, phone, email,
        pickup_type: pickupType,
        pickup_time: pickupType === "scheduled" ? pickupTime : "ASAP",
        items: items.map((i) => ({ name: i.name, quantity: i.quantity, unit_price_cents: i.unit_price_cents, options: i.options })),
      });

      const result = await card.tokenize();
      if (result.status !== "OK") {
        throw new Error(result.errors?.[0]?.message || "Card could not be verified.");
      }

      const { data: pay } = await api.post("/payments/square", { order_id: order.order_id, source_id: result.token });
      clear();
      navigate(`/confirmation/${pay.order_id}`);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    } finally {
      setPaying(false);
    }
  };

  return (
    <div className="min-h-screen bg-black text-white">
      <header className="border-b border-neon-green/20">
        <div className="mx-auto max-w-[1000px] px-5 sm:px-8 h-[72px] flex items-center">
          <Link to="/order" data-testid="back-to-menu" className="flex items-center gap-2 text-neutral-300 hover:text-neon-green transition-colors">
            <ArrowLeft size={20} /> <span className="font-heading text-xl uppercase tracking-wide">Back to Menu</span>
          </Link>
        </div>
      </header>

      <div className="mx-auto max-w-[1000px] px-5 sm:px-8 py-10 grid grid-cols-1 lg:grid-cols-[1fr_380px] gap-10">
        {/* Form */}
        <div className="space-y-8">
          <div>
            <span className="font-graffiti text-neon-yellow text-3xl -rotate-2 inline-block">almost there!</span>
            <h1 className="font-heading text-5xl sm:text-6xl text-neon-green uppercase tracking-tighter leading-none neon-glow-green">Pickup Checkout</h1>
          </div>

          <div className="space-y-4">
            <div>
              <Label className="font-heading text-lg text-neon-green uppercase">Name</Label>
              <Input value={name} onChange={(e) => setName(e.target.value)} data-testid="checkout-name" placeholder="Your name" className="bg-elevated border-neon-green/30 text-white mt-1" />
            </div>
            <div>
              <Label className="font-heading text-lg text-neon-green uppercase">Phone</Label>
              <Input value={phone} onChange={(e) => setPhone(e.target.value)} data-testid="checkout-phone" placeholder="(831) 555-1234" className="bg-elevated border-neon-green/30 text-white mt-1" />
            </div>
            <div>
              <Label className="font-heading text-lg text-neon-green uppercase">Email</Label>
              <Input value={email} onChange={(e) => setEmail(e.target.value)} data-testid="checkout-email" type="email" placeholder="you@email.com" className="bg-elevated border-neon-green/30 text-white mt-1" />
              <p className="font-body text-xs text-neutral-500 mt-1">We&apos;ll email your order confirmation & receipt here.</p>
            </div>
            <div>
              <Label className="font-heading text-lg text-neon-green uppercase">Pickup Time</Label>
              <RadioGroup value={pickupType} onValueChange={setPickupType} className="grid grid-cols-2 gap-3 mt-1">
                <label htmlFor="asap" className="flex items-center gap-2 border border-neon-green/20 px-4 py-3 cursor-pointer hover:border-neon-green">
                  <RadioGroupItem value="asap" id="asap" data-testid="pickup-asap" className="border-neon-green text-neon-green" />
                  <span className="font-body">ASAP (~15-20 min)</span>
                </label>
                <label htmlFor="scheduled" className="flex items-center gap-2 border border-neon-green/20 px-4 py-3 cursor-pointer hover:border-neon-green">
                  <RadioGroupItem value="scheduled" id="scheduled" data-testid="pickup-scheduled" className="border-neon-green text-neon-green" />
                  <span className="font-body">Schedule</span>
                </label>
              </RadioGroup>
              {pickupType === "scheduled" && (
                <Input type="time" value={pickupTime} onChange={(e) => setPickupTime(e.target.value)} data-testid="pickup-time" className="bg-elevated border-neon-green/30 text-white mt-3 w-48" />
              )}
            </div>
          </div>

          <div>
            <Label className="font-heading text-lg text-neon-green uppercase flex items-center gap-2"><Lock size={16} /> Payment</Label>
            {configured ? (
              <div id="card-container" data-testid="card-container" className="min-h-[56px] border border-neon-green/30 bg-white rounded p-3 mt-2" />
            ) : (
              <p className="mt-2 font-body text-sm text-red-400 border border-red-500/40 p-4" data-testid="payment-not-configured">Online payment isn&apos;t set up yet. Please call the restaurant at 831-663-7713 to order.</p>
            )}
            {!sdkReady && configured && <p className="mt-2 font-body text-xs text-neutral-500 flex items-center gap-2"><Loader2 size={14} className="animate-spin" /> Loading secure payment form…</p>}
          </div>
        </div>

        {/* Summary */}
        <div className="lg:sticky lg:top-6 h-fit border border-neon-green/25 bg-elevated p-6">
          <h2 className="font-heading text-3xl text-white uppercase tracking-wide mb-4">Order Summary</h2>
          <div className="space-y-3 max-h-[40vh] overflow-y-auto">
            {items.map((it) => (
              <div key={it.lineId} className="flex justify-between gap-3 text-sm">
                <span className="font-body text-neutral-300">{it.quantity}× {it.name}</span>
                <span className="font-heading text-neon-yellow whitespace-nowrap">{money(it.unit_price_cents * it.quantity)}</span>
              </div>
            ))}
          </div>
          <div className="flex justify-between items-baseline border-t border-neon-green/20 mt-4 pt-4">
            <span className="font-heading text-2xl uppercase">Total</span>
            <span className="font-heading text-3xl text-neon-green neon-glow-green" data-testid="checkout-total">{money(totalCents)}</span>
          </div>
          <Button onClick={handlePay} disabled={paying || !configured} data-testid="pay-now-btn" className="w-full mt-5 font-heading text-2xl tracking-wide bg-neon-green text-black hover:bg-neon-yellow disabled:opacity-40 py-6">
            {paying ? <span className="flex items-center gap-2"><Loader2 className="animate-spin" size={20} /> Processing…</span> : `Pay ${money(totalCents)}`}
          </Button>
          <p className="font-body text-xs text-neutral-500 text-center mt-3">Secured by Square. Cards only.</p>
        </div>
      </div>
    </div>
  );
}
